import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import styles from './UserForm.module.scss';
import { DEBUG } from '../../utils/constants';

import { LanguageContext } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import PasswordStrengthIndicator from '../../utils/PasswordStrengthIndicator/PasswordStrengthIndicator';
import CountrySelector from '../../utils/CountrySelector/CountrySelector';
import IosCompatibleInput from '../../utils/IosCompatibleInput/IosCompatibleInput';

// API
import { createUser, connectUser, isPseudoExists, isEmailExists } from '../../utils/api/usersAPI';
import { logSignIn, logFormChange, logSubscribe, logConnect, reportClientError, logClientDiagnostic } from '../../utils/api/reportAPI';

// Import des traductions 
import translations from '../../utils/translations/UserFormTranslations';
import countries from '../../utils/CountrySelector/allCountries';


const debug = DEBUG;
const logging = true;


const UserForm = () => {

  const { login, clearUserDatas } = useAuth(); // Utilisation du contexte d'authentification
  const navigate = useNavigate();

  // Utilisation du contexte de langue global
  const { language } = useContext(LanguageContext);

  // Accès simplifié aux traductions
  const t = translations[language] || translations.fr;

// ------------------- STATES -------------------------

  const [loading, setLoading] = useState(false); // Ajout d’un état pour éviter plusieurs requêtes
  const [isLogin, setIsLogin] = useState(true); // Ajouter une valeur à tester en localStorage pour voir quel formulaire charger en priorité
  const [formData, setFormData] = useState({
    pseudo: '',
    email: '',
    password: '',
    confirmPassword: '',
    postalCode: '',
    country: '', 
    loginIdentifier: '', // pseudo ou email pour la connexion
    loginPassword: '',
    captcha: false,
    terms: false,
  });
  const [errors, setErrors] = useState({}); // Stockage des erreurs
  const [mustFixErrorsBeforeSubmit, setMustFixErrorsBeforeSubmit] = useState(false);

  // Gestion visibilité du mot de passe
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);


// ------------------- EFFECTS -------------------------

  // LOGGING d'un formulaire resté trop longtemps inactif
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!formData.email) {
        logClientDiagnostic({type: 'form_watchdog', context: 'UserForm > email non rempli après délai', message: 'Champ email resté vide 40s après affichage', })}
    }, 40000); // 40 secondes

    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (!isLogin) {
      logSignIn();
    }
  }, [isLogin]);

  useEffect(() => {
    if (!formData.pseudo) return;

    const timer = setTimeout(async () => {
      const error = await validatePseudo(formData.pseudo);
      setErrors((prev) => ({...prev, pseudo: error,}));
    }, 400);

    return () => clearTimeout(timer);
  }, [formData.pseudo, isLogin]);

  useEffect(() => {
    if (!formData.email) return;

    const timer = setTimeout(async () => {
      const error = await validateEmail(formData.email);
      setErrors((prev) => ({...prev, email: error,}));
    }, 400);

    return () => clearTimeout(timer);
  }, [formData.email, isLogin]);

// ------------------- EVENTS -------------------------

  // const handleChange = async (e) => {
  //   const { name, value, type, checked } = e.target;
  //   let newValue = type === 'checkbox' ? checked : value;
  //   let errorMessage = "";
  
  //   // Appliquer une fonction spécifique si nécessaire
  //   const validationFunctions = {
  //     pseudo: validatePseudo,
  //     email: validateEmail,
  //     password: validatePassword,
  //     confirmPassword: (val) => validateConfirmPassword(val, formData.password),
  //     postalCode: validatePostalCode,
  //     country: validateCountry,
  //     captcha: validateCaptcha,
  //     terms: validateTerms
  //   };
  
  //   // Vérification si c'est une fonction async (API call)
  //   if (validationFunctions[name]) {
  //     errorMessage = name === "pseudo" || name === "email" ? await validationFunctions[name](newValue) : validationFunctions[name](newValue);
  //   }
  
  //   setFormData({
  //     ...formData,
  //     [name]: newValue,
  //   });

  //   setErrors({
  //     ...errors,
  //     [name]: errorMessage, // Mise à jour des erreurs
  //   });

  //   if (debug) logFormChange({ field: name, value: newValue, error: errorMessage });

  // };

  //version sans validation API (=> à part)
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData((prev) => ({...prev, [name]: newValue, }));

    let errorMessage = "";

    const localValidationFunctions = {
      password: validatePassword,
      confirmPassword: (val) => validateConfirmPassword(val, formData.password),
      postalCode: validatePostalCode,
      country: validateCountry,
      captcha: validateCaptcha,
      terms: validateTerms,
    };

    if (localValidationFunctions[name]) {
      errorMessage = localValidationFunctions[name](newValue);
      setErrors((prev) => ({...prev, [name]: errorMessage, }));
    }

    if (logging) { logFormChange({field: name, value: newValue, error: errorMessage,});}
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true); // Bloque le formulaire en attendant la réponse

    if (isLogin) {
      try {
        const loginId = cleanInput(formData.loginIdentifier);
        const loginPwd = formData.loginPassword;
        const userData = await connectUser(loginId, loginPwd);
        if (userData.authToken) {
          login(userData); // Stocke les infos utilisateur dans le contexte
        } else {
          setErrors((prev) => ({ ...prev, general: t.invalidCredentials }));
          if (logging) reportClientError( { type: 'Connexion', context: 'UserForm > handleSubmit', message: t.invalidCredentials }, formData );
          setLoading(false);
        }
      } catch (error) {
        setErrors((prev) => ({ ...prev, general: t.serverError }));
        if (logging) reportClientError( { type: 'Connexion', context: 'UserForm > handleSubmit', message: t.serverError }, formData );
        setLoading(false);
      }
    } else {
      // Petit délai pour laisser le DOM finir les blur/input
      //await new Promise((resolve) => setTimeout(resolve, 50));
      
      // Traitement du formulaire d'inscription
      // Vérifier tous les champs avant soumission
      const newErrors = {
        pseudo: await validatePseudo(formData.pseudo),
        email: await validateEmail(formData.email),
        password: validatePassword(formData.password),
        postalCode: validatePostalCode(formData.postalCode),
        country: validateCountry(formData.country),
        confirmPassword: validateConfirmPassword(formData.confirmPassword, formData.password),
        captcha: validateCaptcha(formData.captcha),
        terms: validateTerms(formData.terms),
      };

      setErrors(newErrors);

      // Vérifier s'il y a des erreurs
      const hasErrors = Object.values(newErrors).some(error => error !== "");

      if (!hasErrors) {
        try {
          const userData = await createUser(formData.email, formData.pseudo, formData.password, formData.confirmPassword, formData.postalCode, formData.country);
          if (userData.authToken) {
            clearUserDatas();
            login(userData); // Stocke les infos utilisateur dans le contexte
            // console.log("Formulaire soumis avec succès 🎉", formData);
          } else {
            setErrors((prev) => ({ ...prev, general: t.registrationError }));
            if (logging) reportClientError( { type: 'Inscription', context: 'UserForm > handleSubmit', message: t.registrationError, stack: errors }, formData );
          }
        } catch (error) {
          setErrors((prev) => ({ ...prev, general: t.serverError }));
          if (logging) reportClientError( { type: 'Inscription', context: 'UserForm > handleSubmit', message: t.serverError, stack: errors }, formData );
        } finally {
          setLoading(false);
          setMustFixErrorsBeforeSubmit(false);
        }
      } else {
        setMustFixErrorsBeforeSubmit(true);
        if (debug) console.log(t.fixErrorsBeforeSubmit);
        if (debug) console.log(errors);
        if (logging) { reportClientError(
          {
            type: 'Erreur de saisie inscription',
            context: 'UserForm > handleSubmit > validation',
            message: t.fixErrorsBeforeSubmit,
            stack: {
              errors: newErrors,
              errorFields: Object.entries(newErrors).filter(([, message]) => message !== "").map(([field]) => field),
            },
          }, formData);
  }
        setLoading(false);
      }
      
    }
  };


// ------------------- FUNCTIONS -------------------------

const cleanInput = (str) =>
  (str || '')
    .replace(/[\u200B-\u200D\uFEFF\u2060]/g, '') // caractères invisibles
    .normalize("NFD")                            // décomposition accents
    .replace(/[\u0300-\u036f]/g, '')             // suppression accents
    .trim();

const validatePseudo = async (value) => {
  let trimmedValue = cleanInput(value); // Supprime les espaces en début et fin
  let errorMessage = "";

  // Vérification du format (autoriser lettres, chiffres, tirets, underscore et caractères européens)
  const pseudoRegex = /^[A-Za-z0-9À-ÖØ-öø-ÿ_-]+$/;
  if (!pseudoRegex.test(trimmedValue)) {
    errorMessage = t.invalidUsernameFormat;
  } else {
    // Vérification avec l'API si le pseudo est déjà pris
    const exists = await isPseudoExists(trimmedValue);
    if (exists.exists === true) {
      errorMessage = t.usernameTaken;
    }
  }

  return errorMessage; // Retourne "" si tout est bon, sinon un message d'erreur
};

const validateEmail = async (value) => {
  let trimmedValue = cleanInput(value); // Supprime les espaces en début et fin
  let errorMessage = "";

  // Vérification du format email
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(trimmedValue)) {
    errorMessage = t.invalidEmailFormat;
  } else {
    // Vérification avec l'API si l'email est déjà utilisé
    const exists = await isEmailExists(trimmedValue);
    if (exists && exists.exists === true) {
      errorMessage = t.emailTaken;
    }
  }

  return errorMessage; // Retourne "" si tout est bon, sinon un message d'erreur
};

const validatePassword = (value) => {
  let errorMessage = "";
  
  // Vérifications spécifiques
  if (value.length < 12) {
    errorMessage = t.passwordTooShort;
  } else if (!/[A-Z]/.test(value)) {
    errorMessage = t.passwordMissingUppercase;
  } else if (!/[a-z]/.test(value)) {
    errorMessage = t.passwordMissingLowercase;
  } else if (!/[0-9]/.test(value)) {
    errorMessage = t.passwordMissingNumber;
  } else if (!/[\W_]/.test(value)) {
    errorMessage = t.passwordMissingSpecial;
  }

  return errorMessage; // Retourne "" si tout est bon, sinon un message d'erreur
};

const validateConfirmPassword = (value, password) => {
  if (!value) {
    return t.confirmPasswordRequired;
  }
  if (value !== password) {
    return t.passwordMismatch;
  }
  return ""; // Pas d'erreur
};

const validatePostalCode = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return t.postalCodeRequired;

  const postalCodeRegexes = [
    /^\d{5}$/, // France, Allemagne, US simple
    /^[A-Za-z]\d[A-Za-z][\s-]?\d[A-Za-z]\d$/, // Canada
    /^[A-Za-z]{1,2}\d[A-Za-z0-9]?\s?\d[A-Za-z]{2}$/, // UK
    /^\d{4}$/ // Belgique, Suisse
  ];

  const isValidPostalCode = postalCodeRegexes.some(regex => regex.test(trimmed));

  if (!isValidPostalCode) {
    return t.invalidPostalCode;
  }

  return "";
};

const validateCountry = (code) => {
  const found = countries.find((c) => c.code === code);
  return found ? "" : t.countryRequired;
};

const validateCaptcha = (value) => value ? "" : t.captchaRequired;

const validateTerms = (value) => value ? "" : t.acceptTermsRequired;

const togglePasswordVisibility = (passwordField) => {
  if (passwordField === 'password') {
    setShowPassword(!showPassword);
  } else if (passwordField === 'confirmPassword') {
    setShowConfirmPassword(!showConfirmPassword);
  } else if (passwordField === 'loginPassword') {
    setShowLoginPassword(!showLoginPassword);
  }
};

const handleFocus = (e) => {
  // Force le navigateur à reconsidérer les attributs lors du focus
  // e.target.setAttribute('autocomplete', 'new-password');
  // e.target.setAttribute('autocorrect', 'off');

  if (debug) logFormChange({
    field: e.target.name,
    action: 'focus',
    origin: e.nativeEvent.inputType || 'unknown',
  });
};

// ------------------- DISPLAY -------------------------

  return (
    <div className={styles.authContainer}>
      <h2 className={styles.title}>{t.accessApp}</h2>
      <p className={styles.subTitle}>{t.accountRequired}</p>
                 
          
        {isLogin ? (
          <form key="login-form" onSubmit={handleSubmit} className={styles.authForm} autoComplete="on">
            <div className={styles.formGroup}>
              <label htmlFor="loginIdentifier">{t.usernameOrEmail}</label>
              {/* <input type="text" id="loginIdentifier" name="loginIdentifier" value={formData.loginIdentifier} onChange={handleChange} required /> */}
              <IosCompatibleInput
                type="text"
                id="loginIdentifier"
                name="loginIdentifier"
                autoComplete="username"
                value={formData.loginIdentifier}
                onFocus={handleFocus}
                onChange={handleChange}
                debounce={0} // optionnel, par défaut 300ms
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="loginPassword">{t.password}</label>
              <div className={styles.passwordInputContainer}>
                <input type={showLoginPassword ? "text" : "password"} id="loginPassword" name="loginPassword" autoComplete="current-password" value={formData.loginPassword} onChange={handleChange} required />
                <button type="button" onClick={() => togglePasswordVisibility('loginPassword')} className={styles.togglePasswordButton} aria-label={showLoginPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}>
                  {showLoginPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              
            </div>
            {errors.general && <p className={styles.error}>{errors.general}</p>}
            <button className={styles.button} type="submit" disabled={loading}>
              {loading ? t.connecting : t.login}
            </button>
            <p className={styles.toggleText}>
              <button type="button" onClick={() => navigate("/reset-request")} className={styles.toggleButton}>{t.forgotPassword}</button>
            </p>
          </form>
        ) : (
          <form key="signup-form" onSubmit={handleSubmit} className={styles.authForm} autoComplete="off">
            <div className={styles.formGroup}>
              <label htmlFor="pseudo">{t.username}</label>
              <IosCompatibleInput
                type="text" 
                id="pseudo" 
                name="pseudo" 
                value={formData.pseudo}
                onChange={handleChange}
                debounce={0} // optionnel, par défaut 300ms
              />
                
              {errors.pseudo && <p className={styles.error}>{errors.pseudo}</p>}
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="email">{t.email}</label>
              <IosCompatibleInput 
                type="email" 
                id="email" 
                name="email" 
                value={formData.email} 
                onChange={handleChange} 
                required />
              {errors.email && <p className={styles.error}>{errors.email}</p>}
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="postalCode">{t.postalCode}</label>
              {/* <input type="text" autoComplete="new-password" id="postalCode" name="postalCode" value={formData.postalCode} onChange={handleChange} required /> */}
              <IosCompatibleInput
                type="text"
                id="postalCode" 
                name="postalCode"
                autoComplete="postal-code"
                value={formData.postalCode}
                onChange={handleChange}
                debounce={0} // optionnel, par défaut 300ms
              />
              {errors.postalCode && <p className={styles.error}>{errors.postalCode}</p>}
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="country">{t.country}</label>
              <CountrySelector value={formData.country} onChange={(selectedCode) => handleChange({ target: { name: 'country', value: selectedCode } }) } />
              {errors.country && <p className={styles.error}>{errors.country}</p>}
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="password">{t.password}</label>
              {/* <input type="password" id="password" name="password" value={formData.password} onChange={handleChange} required /> */}
              <div className={styles.passwordInputContainer}>
                <input type={showPassword ? "text" : "password"} autoComplete="new-password" id="password" name="password" value={formData.password} onChange={handleChange} required />
                <button type="button" onClick={() => togglePasswordVisibility('password')} className={styles.togglePasswordButton} aria-label={showLoginPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}>
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              
              {errors.password && <p className={styles.error}>{errors.password}</p>}
              <PasswordStrengthIndicator password={formData.password} />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="confirmPassword">{t.confirmPassword}</label>
              <input type="password" autoComplete="new-password" id="confirmPassword" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} required />
              {errors.confirmPassword && <p className={styles.error}>{errors.confirmPassword}</p>}
            </div>
            <div className={styles.checkboxGroup}>
              <input type="checkbox" id="captcha" name="captcha" checked={formData.captcha} onChange={handleChange} />
              <label htmlFor="captcha">{t.captchaLabel}</label>
            </div>
            {errors.captcha && <p className={styles.error}>{errors.captcha}</p>}
            <div className={styles.checkboxGroup}>
              <input type="checkbox" id="terms" name="terms" checked={formData.terms} onChange={handleChange} />
              <label htmlFor="terms">{t.iHaveRead}<a className={styles.anchor} onClick={() => navigate("/conditions")}>{t.termsAndConditions}</a>{t.and}<a className={styles.anchor} onClick={() => navigate("/privacy")}>{t.privacy}</a></label>
            </div>
            {errors.terms && <p className={styles.error}>{errors.terms}</p>}
            {mustFixErrorsBeforeSubmit && <p className={styles.error}>{t.fixErrorsBeforeSubmit}</p>}
            <button className={styles.button} type="submit" disabled={loading}>{t.register}</button>
          </form>
        )}
      
      <div className={styles.toggleContainer}>
        {isLogin ? (
          <p className={styles.toggleText}>
            {t.noAccount}{' '}
            <button onClick={() => setIsLogin(false)} className={styles.toggleButton}>
              {t.createAccount}
            </button>
          </p>
        ) : (
          <p className={styles.toggleText}>
            {t.alreadyHaveAccount}{' '}
            <button onClick={() => setIsLogin(true)} className={styles.toggleButton}>
              {t.goToLogin}
            </button>
          </p>
        )}
      </div>
    </div>
  );
};

export default UserForm;
